/**
 * Rattachement d'une tâche à son lot, pour le tri « Lot » de la vue Liste.
 *
 * Le lot d'une tâche est son ancêtre le plus proche de `kind: 'phase'` — elle-même si c'en est
 * une. La liste étant paginée, le parent d'une tâche peut vivre sur une autre page : on remonte
 * donc dans l'index de la page **et** dans celui de toutes les phases du projet (chargées à
 * part), ce qui couvre une tâche directement rangée dans un lot resté sur une autre page. Seule
 * une sous-tâche dont la tâche parente est hors page ET hors phases reste sans lot connu.
 */

/**
 * @param {{ id: string, kind?: string, parentId?: string|null }} task
 * @param {Map<string, { id: string, kind?: string, parentId?: string|null }>} byId
 * @returns {object|null} La phase-lot, ou `null`.
 */
export function lotOf(task, byId) {
  const seen = new Set();
  let current = task;
  while (current && !seen.has(current.id)) {
    if (current.kind === 'phase') return current;
    seen.add(current.id);
    current = current.parentId ? byId.get(current.parentId) : null;
  }
  return null;
}

const collator = new Intl.Collator('fr', { numeric: true, sensitivity: 'base' });

/**
 * Comparateur « par lot » : titre du lot en ordre naturel (« Lot 2 » avant « Lot 10 »), puis
 * référence de la tâche pour départager les tâches d'un même lot. Les tâches sans lot restent en
 * fin de liste quel que soit le sens, comme les valeurs vides des autres colonnes.
 * @param {'asc'|'desc'} dir
 * @param {(task: object) => object|null} resolve
 */
export function compareByLot(dir, resolve) {
  const sign = dir === 'asc' ? 1 : -1;
  return (a, b) => {
    const la = resolve(a);
    const lb = resolve(b);
    if (!la && !lb) return a.number - b.number;
    if (!la) return 1;
    if (!lb) return -1;
    const byLot = collator.compare(la.title, lb.title) || la.number - lb.number;
    if (byLot !== 0) return sign * byLot;
    // Le lot lui-même en tête de ses tâches, dans les deux sens.
    if (a.id === la.id) return -1;
    if (b.id === lb.id) return 1;
    return a.number - b.number;
  };
}
